/**
 * Dialog "Nuova famiglia" per supplier_product_families.
 *
 * Le famiglie sono il pivot tra materiali e sconti:
 *   materials.family_id → supplier_product_families.id → customer_discounts.family_id
 *
 * Es: famiglia "Lastre Habito" del fornitore Saint Gobain raggruppa tutti i materiali
 * della linea Habito. L'org può applicare uno sconto cumulativo a TUTTA la famiglia
 * (es. "30+5") che si propaga in `net_price` di ogni materiale.
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus } from 'lucide-react';
import { useCreateFamily } from '@/hooks/useSupplierManagement';

interface CreateFamilyDialogProps {
  supplierId: string;
  supplierName?: string;
  /** Stato open controllato (omesso → gestito internamente, richiede `showTrigger`). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Callback dopo creazione. Riceve `{ id, name }` della nuova famiglia. */
  onCreated?: (family: { id: string; name: string }) => void;
  /** Pre-fill nome (es. autocomplete). */
  initialName?: string;
  /** Mostra il bottone trigger integrato (per /suppliers detail). */
  showTrigger?: boolean;
}

const CreateFamilyDialog: React.FC<CreateFamilyDialogProps> = ({
  supplierId,
  supplierName,
  open: openProp,
  onOpenChange,
  onCreated,
  initialName = '',
  showTrigger = false,
}) => {
  const [openInternal, setOpenInternal] = useState(false);
  const open = openProp ?? openInternal;
  const setOpen = (v: boolean) => {
    if (onOpenChange) onOpenChange(v);
    else setOpenInternal(v);
  };

  const [code, setCode] = useState('');
  const [name, setName] = useState(initialName);
  const [category, setCategory] = useState('');
  const [brand, setBrand] = useState('');
  const [description, setDescription] = useState('');
  const create = useCreateFamily();

  useEffect(() => {
    if (open) {
      setName(initialName);
      setCode('');
      setCategory('');
      setBrand('');
      setDescription('');
    }
  }, [open, initialName]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim() || !name.trim()) return;
    create.mutate(
      {
        supplier_id: supplierId,
        code,
        name,
        category: category || null,
        brand: brand || null,
        description: description || null,
      },
      {
        onSuccess: (data) => {
          if (data) onCreated?.({ id: data.id, name: data.name });
          setOpen(false);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {showTrigger && (
        <DialogTrigger asChild>
          <Button size="sm" variant="outline">
            <Plus className="mr-1 h-4 w-4" /> Nuova famiglia
          </Button>
        </DialogTrigger>
      )}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nuova famiglia</DialogTitle>
          <DialogDescription>
            {supplierName
              ? `Crea una famiglia di prodotti per ${supplierName}. Ti permetterà di applicare uno sconto cumulativo a tutti i materiali della famiglia.`
              : 'Crea una famiglia di prodotti per il fornitore. Ti permetterà di applicare uno sconto cumulativo a tutti i materiali della famiglia.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="fam-code">Codice*</Label>
              <Input
                id="fam-code"
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder='es. "HABITO", "FIRELINE"'
                autoFocus
              />
              <p className="text-[10px] text-muted-foreground mt-1">Identificatore breve, in maiuscolo.</p>
            </div>
            <div>
              <Label htmlFor="fam-name">Nome*</Label>
              <Input
                id="fam-name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="es. Lastre Habito alta resistenza"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="fam-cat">Categoria</Label>
              <Input
                id="fam-cat"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="es. Lastre, Profili, Isolanti"
              />
            </div>
            <div>
              <Label htmlFor="fam-brand">Brand</Label>
              <Input
                id="fam-brand"
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                placeholder="es. Gyproc, Knauf"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="fam-desc">Descrizione</Label>
            <Textarea
              id="fam-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Note descrittive (opzionale)"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Annulla
            </Button>
            <Button type="submit" disabled={!code.trim() || !name.trim() || create.isPending}>
              {create.isPending ? 'Creo…' : 'Crea famiglia'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateFamilyDialog;
